// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  MdOutlineLibraryBooks,
  MdOutlineContentPasteSearch,
  MdArrowBack,
} from "react-icons/md";
import axios from "../utils/interceptor";

import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import "./common-styles.css";
import { downloadDoc } from "../utils/urls";
import { CorrectionsTable } from "./CorrectionsTable";

const cleanHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "th",
      "td",
      "p",
      "strong",
      "em",
      "u",
      "ol",
      "ul",
      "li",
      "br",
      "span",
      "div",
      "img",
      "sub",
      "sup",
    ],
    ALLOWED_ATTR: [
      "style",
      "align",
      "border",
      "cellpadding",
      "cellspacing",
      "colspan",
      "rowspan",
      "width",
      "height",
      "valign",
      "halign",
      "src",
      "alt",
      "class",
    ],
    ADD_TAGS: ["sub", "sup"],
    ADD_ATTR: ["style"],
  });
};

const AInizedDocResults = () => {
  const location = useLocation();
  const navigate = useNavigate(); // for back/fallback navigation
  const { suggestion, originalHtml, headers, footers } = location.state || {};
  const [isDownloadLoading, setDownloadLoading] = useState(false);
  const [chunks, setChunks] = useState<
    { corrected: string; issues: string[] }[]
  >([]);
  const [htmlOutput, setHtmlOutput] = useState("");

  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);
  const handleMenuClick = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  // Back handler with safe fallback
  const onBack = () => {
    if (window.history.length > 2) {
      navigate("/");
    } else {
      navigate("/", { replace: true }); // adjust to your list/import route
    }
  };

  const processTableHTML = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const tables = doc.querySelectorAll("table");
    tables.forEach((table) => {
      const cells = table.querySelectorAll("td, th");
      cells.forEach((cell) => {
        if (!cell.hasAttribute("style")) {
          cell.setAttribute("style", "border: 1px solid #ddd; padding: 4px;");
        } else if (!cell.getAttribute("style")?.includes("border")) {
          cell.setAttribute(
            "style",
            cell.getAttribute("style") + " border: 1px solid #ddd;"
          );
        }
      });

      if (!table.hasAttribute("style")) {
        table.setAttribute("style", "border-collapse: collapse; width: 100%;");
      } else if (!table.getAttribute("style")?.includes("border-collapse")) {
        table.setAttribute(
          "style",
          table.getAttribute("style") + " border-collapse: collapse;"
        );
      }
    });

    return doc.body.innerHTML;
  };

  useEffect(() => {
    DOMPurify.addHook("uponSanitizeElement", (node, data) => {
      const allowedTags = [
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "ul",
        "ol",
        "li",
        "img",
      ];
      if (allowedTags.includes(data.tagName)) {
        // For images, preserve src and alt attributes
        if (data.tagName === "img") {
          node.setAttribute("style", "max-width: 100%; height: auto;");
        } else {
          node.removeAttribute("class");
          node.removeAttribute("style");
        }
      }
    });
    axios
      .get(suggestion.download_urls.corrections_html)
      .then((res) => {
        console.log(res);
        setHtmlOutput(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (!suggestion?.issues) return;

    const parsedChunks = suggestion.issues.map((item: any) => ({
      corrected: processTableHTML(wrapHTML(item.corrected)),
      issues: extractIssues(item.issues),
    }));

    setChunks(parsedChunks);
  }, [suggestion]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Download corrected content as DOCX
  const handleDownloadFullDocx = async () => {
    console.log(suggestion);

    try {
      setDownloadLoading(true);
      // http://localhost:8000
      // https://apidocumentor-ai.mydrreddys.com
      window.open(
        `${process.env.REACT_APP_API_URL}${suggestion?.download_urls?.corrected_docx}`,
        "__blank"
      );
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to generate DOCX download.");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Download corrected content as PDF (via DOCX)
  const handleDownloadFullPdf = async () => {
    if (!suggestion?.corrected_version) {
      alert("No content to download.");
      return;
    }

    try {
      const fullHtml = suggestion.corrected_version;

      // Step 1: HTML -> DOCX
      const docxResponse = await axios.post(
        `/convert-html-to-docx`,
        { html: fullHtml },
        { responseType: "blob" }
      );

      // Step 2: DOCX -> PDF
      const formData = new FormData();
      formData.append(
        "file",
        new Blob([docxResponse.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }),
        "Corrected_Document.docx"
      );

      const pdfResponse = await axios.post(`/convert-docx-to-pdf`, formData, {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });

      // Step 3: Trigger PDF download
      downloadBlob(pdfResponse.data, "Corrected_Document.pdf");
    } catch (error) {
      console.error("PDF download failed:", error);
      alert("Failed to generate PDF download.");
    }
  };

  // NEW: Download as HTML file with all content preserved
  const handleDownloadHtml = () => {
    if (!suggestion?.corrected_version) {
      alert("No content to download.");
      return;
    }

    try {
      // Create a complete HTML document
      const fullHtmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Corrected Document</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
    table, th, td { border: 1px solid #ddd; }
    th, td { padding: 8px; text-align: left; }
    img { max-width: 100%; height: auto; }
  </style>
</head>
<body>
  ${suggestion.corrected_version}
</body>
</html>`;

      // Create blob and download
      const blob = new Blob([fullHtmlContent], { type: "text/html" });
      downloadBlob(blob, "Corrected_Document.html");
    } catch (error) {
      console.error("HTML download failed:", error);
      alert("Failed to generate HTML download.");
    }
  };

  const unescapeHTML = (html: string): string => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };

  function wrapHTML(html: string): string {
    const trimmed = html.trim();

    if (trimmed.startsWith("<tr") && !trimmed.includes("<table")) {
      return `<table><tbody>${trimmed}</tbody></table>`;
    }

    if (
      (trimmed.includes("<td") || trimmed.includes("<th")) &&
      !trimmed.includes("<tr")
    ) {
      return `<table><tbody><tr>${trimmed}</tr></tbody></table>`;
    }

    if (trimmed.startsWith("<li")) {
      return `<ul>${trimmed}</ul>`;
    }

    if (!/^<(table|ul|ol|p|h[1-6]|div|span)/i.test(trimmed)) {
      return `<div>${trimmed}</div>`;
    }

    return trimmed;
  }

  const extractIssues = (html: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const textContent = doc.body.textContent || "";

    return textContent
      .replace("[", "")
      .replace("]", "")
      .split(/[\n\r]+/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  };

  const getDistinctColor = (index: number) => {
    const colors = [
      "#ffe0e0",
      "#e0ffe0",
      "#e0f7ff",
      "#fff3e0",
      "#f8e0ff",
      "#ffe0f8",
      "#e0f3ff",
      "#f5ffe0",
      "#ffe6cc",
      "#e6ccff",
    ];
    return colors[index % colors.length];
  };

  useEffect(() => {
    if (!suggestion?.issues) return;

    function wrapHTML(html: string): string {
      const trimmed = html.trim();

      if (!/^<(table|ul|ol|p|h[1-6])>/i.test(trimmed)) {
        if (trimmed.startsWith("<tr")) return `<table>${trimmed}</table>`;
        if (trimmed.startsWith("<li")) return `<ul>${trimmed}</ul>`;
        return `<div>${trimmed}</div>`;
      }

      return trimmed;
    }

    const parsedChunks = suggestion.issues.map((item: any) => ({
      corrected: wrapHTML(item.corrected),
      issues: extractIssues(item.issues),
    }));

    setChunks(parsedChunks);
  }, [suggestion]);

  return (
    <div className="results-container">
      {/* <div className="chunk-pair-header">

        <h3 className="section-heading">
          <MdOutlineLibraryBooks /> Corrected Version
        </h3>
        <h3 className="section-heading" style={{ marginLeft: "auto" }}>
          <MdOutlineContentPasteSearch /> Issues Found{" "}
          <span>
            
            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleDownloadFullDocx();
                  handleMenuClose();
                }}
              >
                Download as DOCX
              </MenuItem>
              <MenuItem
                onClick={() => {
                  // handleDownloadFullPdf();
                  handleMenuClose();
                }}
              >
                Download as PDF
              </MenuItem>
            </Menu>
          </span>
        </h3>
      </div> */}
      <div className="d-flex align-items-start justify-content-between">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to documents"
          title="Back to documents"
          className="btn btn-ghost"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid rgba(0,0,0,0.1)",
            background: "white",
            cursor: "pointer",
          }}
        >
          <MdArrowBack size={18} />
          <span className="hidden sm:inline">Back</span>
        </button>
        {/* <CorrectionsTable correctionsData={suggestion?.corrections} /> */}
        <div className="html-content-wrapper">
          <div dangerouslySetInnerHTML={{ __html: htmlOutput }} />
        </div>
        <Button
          startIcon={
            isDownloadLoading ? (
              <CircularProgress size={14} />
            ) : (
              <DownloadOutlinedIcon />
            )
          }
          onClick={handleDownloadFullDocx}
          style={{ fontSize: "11px", padding: "4px 8px" }}
          size="small"
        >
          Download
        </Button>
      </div>

      {/* <div className="chunk-pair-scrollable">
        {chunks.length === 0 ? (
          <p>No data available.</p>
        ) : (
          chunks.map((chunk, index) => (
            <div key={index} className="chunk-pair">
              <div className="result-left-section">
                <div className="word-document">
                  {chunk.corrected ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(
                          unescapeHTML(wrapHTML(chunk.corrected))
                        ),
                      }}
                    />
                  ) : (
                    <p>No content</p>
                  )}
                </div>
              </div>

              <div className="result-right-section">
                {chunk.issues.length > 0 ? (
                  <div className="issue-card">
                    {chunk.issues.map((issue, i) => (
                      <div
                        key={i}
                        className="issue-item"
                        style={{
                          backgroundColor: getDistinctColor(i),
                          padding: "8px",
                          margin: "4px 0",
                          borderRadius: "4px",
                        }}
                      >
                        {issue}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p></p>
                )}
              </div>
            </div>
          ))
        )}
      </div> */}
    </div>
  );
};

export default AInizedDocResults;
